import type { Fare, FlightCard, Sectors } from '@/src/types/flight'
import { parseMulticitySectors } from '../srp/searchUtils'
import { isFlyin } from '../generalUtils/isFlyin'
import { DeviceType } from '@/src/types/deviceType'
import { MOBILE } from '@/src/constants/deviceTypes'

// ---------- CreateItinerary V2 request types (POST /itin/v2/itinerary/create) ----------

export interface FareGroupInfoV2 {
    ctBrandName?: string
    fareGroup?: string
}

export interface FareRequestV2 {
    basePrice: number
    price: number
    fareKey: string
    solutionId?: string
    comboFBC?: string
    fareType?: string
    fareGroupInformation?: FareGroupInfoV2
}

export interface FlightParamsV2 {
    from: string
    to: string
    departDate: number // epoch ms
    cabinType: string
    fares: FareRequestV2[]
}

export interface RequestMetaV2 {
    tripType: 'ONE_WAY' | 'ROUND_TRIP' | 'MULTI_CITY'
    domain: string
    sourceType: string
    sid: string
    utmCurrency?: string
    userId?: string
    utmSource?: string
}

export interface CreateItineraryRequestV2 {
    flightParams: FlightParamsV2[]
    paxInfo: { adults: number; children: number; infants: number }
    itineraryMeta: RequestMetaV2
}

// ---------- Helpers ----------

/**
 * Builds a flat V2 fare entry from the card-level fare (combined across all MC legs).
 *
 * The combined fare lives on the first leg, so `basePrice`/`price` carry their real
 * values only when `zeroPrice` is false; subsequent legs pass `zeroPrice` to emit 0.
 */
function buildFareRequestV2 (fareObj: Fare, dfd: string, zeroPrice = false): FareRequestV2 {
    const fare: FareRequestV2 = {
        basePrice: zeroPrice ? 0 : fareObj.bp,
        price: zeroPrice ? 0 : fareObj.pr,
        fareKey: fareObj.fk,
        solutionId: fareObj.solutionId,
        comboFBC: fareObj.fb,
        fareType: dfd === 'R' ? 'REFUNDABLE' : 'NON_REFUNDABLE'
    }
    const fgi = fareObj.fareGroupInformation
    if (fgi && (fgi.ctBrandName || fgi.fareGroup)) {
        fare.fareGroupInformation = {
            ctBrandName: fgi.ctBrandName,
            fareGroup: fgi.fareGroup
        }
    }
    return fare
}

/** DD/MM/YYYY -> epoch ms (local midnight). Returns 0 when unparseable. */
function ddmmyyyyToEpochMs (date: string): number {
    const [dd, mm, yyyy] = (date || '').split('/').map(Number)
    if (!dd || !mm || !yyyy) return 0
    return new Date(yyyy, mm - 1, dd).getTime()
}

// ---------- Payload builder ----------

/**
 * Builds the CreateItinerary V2 payload for a MULTI_CITY booking.
 *
 * One `FlightParamsV2` entry is emitted per leg (per `sectorKeys` entry), in journey
 * order. The selected multicity card carries a single combined fare, so the same
 * fare (fareKey / solutionId / comboFBC) is repeated on every leg, as per the V2
 * contract for combined bookings.
 */
export function buildMulticityItineraryPayloadV2 ({
    urlQueryObj,
    flight,
    jsons,
    sectors,
    utmCurrency,
    deviceType
}: {
    urlQueryObj: Record<string, string>
    flight: FlightCard
    jsons: any
    sectors: Sectors | undefined
    utmCurrency: string
    deviceType: DeviceType
}): CreateItineraryRequestV2 {
    const cabinType = urlQueryObj.class?.toUpperCase().replace(/\s+/g, '_') || 'ECONOMY'
    const fareObj: Fare = flight?.priceBreakup?.fare || ({} as Fare)

    // Fallback leg info from the URL (dep{n}/arr{n}/depdate{n}) when a sector is missing.
    const { sectors: querySectors } = parseMulticitySectors(urlQueryObj)

    const flightParams: FlightParamsV2[] = (flight?.sectorKeys || []).map((key, index) => {
        const sector = sectors?.[key]
        const querySector = querySectors[index]
        return {
            from: sector?.firstDeparture?.airportCode || querySector?.from || '',
            to: sector?.lastArrival?.airportCode || querySector?.to || '',
            departDate: sector?.firstDeparture?.timestamp || ddmmyyyyToEpochMs(querySector?.depart_date),
            cabinType,
            fares: [buildFareRequestV2(fareObj, flight?.dfd || '', index !== 0)]
        }
    })

    return {
        flightParams,
        paxInfo: {
            adults: Number(urlQueryObj.adults || 1),
            children: Number(urlQueryObj.childs || 0),
            infants: Number(urlQueryObj.infants || 0)
        },
        itineraryMeta: {
            tripType: 'MULTI_CITY',
            domain: jsons?.domain,
            sourceType: isFlyin() ? 'B2C' : deviceType === MOBILE ? 'MOBILE' : 'B2C',
            sid: jsons?.psid || '',
            utmCurrency,
            ...(jsons?.userInfo && { userId: jsons.userInfo.usrId }),
            utmSource: ''
        }
    }
}
